/**
 * Content-based check for uploaded files. The extension and `file.type` are
 * both chosen by the sender, so a renamed executable or script passes them.
 * The first bytes of the file (its signature) are what decide the real type.
 */
export type FileKind = 'jpg' | 'png' | 'pdf' | 'xlsx' | 'xls' | 'csv';

/** Extension → kind of content it must hold. `jpeg` is the same as `jpg`. */
const EXTENSION_KIND: Record<string, FileKind> = {
  jpg: 'jpg',
  jpeg: 'jpg',
  png: 'png',
  pdf: 'pdf',
  xlsx: 'xlsx',
  xls: 'xls',
  csv: 'csv',
};

const SIGNATURES: Partial<Record<FileKind, number[][]>> = {
  jpg: [[0xff, 0xd8, 0xff]],
  png: [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  pdf: [[0x25, 0x50, 0x44, 0x46, 0x2d]], // %PDF-
  xlsx: [[0x50, 0x4b, 0x03, 0x04]], // zip container
  xls: [[0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]], // OLE2
};

const HEAD_BYTES = 1024;

/** Characters that have no place in a file name and are used for XSS / path tricks. */
const UNSAFE_NAME = /[<>"'`\\/\u0000-\u001f]/;

/** Active content a PDF can carry: scripts, auto-run actions, launchers, embedded files. */
const PDF_ACTIVE = /\/(JavaScript|JS|OpenAction|AA|Launch|EmbeddedFiles?|RichMedia|SubmitForm|ImportData|XFA)\b/;

/** Markup or script hidden inside an image (polyglot files). */
const IMAGE_MARKUP = /<\s*(script|svg|html|iframe|object|embed|img|body)\b|javascript\s*:|\bon(error|load)\s*=/i;

/** Whole file as latin1 text (1 char per byte) for marker scanning. */
async function asText(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let out = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    out += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return out;
}

export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

/**
 * True when the file's extension is a known one and its content matches it.
 * Anything with an unknown extension, or content that is not what the
 * extension claims, is unsafe.
 */
export async function isSafeUpload(file: File): Promise<boolean> {
  const kind = EXTENSION_KIND[fileExtension(file.name)];
  if (!kind || UNSAFE_NAME.test(file.name)) return false;

  const head = new Uint8Array(await file.slice(0, HEAD_BYTES).arrayBuffer());
  if (kind === 'csv') {
    // Plain text: a NUL byte means binary content under a .csv name.
    return !head.includes(0);
  }
  if (head.length === 0) return false;
  const typeOk = (SIGNATURES[kind] ?? []).some((sig) => sig.every((b, i) => head[i] === b));
  if (!typeOk) return false;

  if (kind === 'pdf') return !PDF_ACTIVE.test(await asText(file));
  if (kind === 'jpg' || kind === 'png') return !IMAGE_MARKUP.test(await asText(file));
  return true;
}
